import React, { useState, useEffect } from 'react';
import UnknownLocationReports from '../components/Admin/UnknownLocationReports';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Menu,
  MenuItem,
  Chip,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import {
  getSchools,
  createSchool,
  updateSchool,
  deactivateSchool,
  getAllCountries,
  getCouncils,
  getBoards,
  AdminSchoolDTO,
  AdminCountryDTO,
  AdminCouncilDTO,
  AdminBoardDTO,
} from '../services/adminLocationService';

const SchoolManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [schools, setSchools] = useState<AdminSchoolDTO[]>([]);
  const [countries, setCountries] = useState<AdminCountryDTO[]>([]);
  const [councils, setCouncils] = useState<AdminCouncilDTO[]>([]);
  const [boards, setBoards] = useState<AdminBoardDTO[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSchool, setSelectedSchool] = useState<AdminSchoolDTO | null>(null);
  const [selectedCouncilId, setSelectedCouncilId] = useState<number | ''>('');
  const [formName, setFormName] = useState('');
  const [formSchoolType, setFormSchoolType] = useState('');
  const [formBoardId, setFormBoardId] = useState<number | ''>('');
  const [formAddress1, setFormAddress1] = useState('');
  const [formAddress2, setFormAddress2] = useState('');
  const [formCity, setFormCity] = useState('');
  const [formPostcode, setFormPostcode] = useState('');
  const [filterCountryId, setFilterCountryId] = useState<number | ''>('');
  const [filterCouncilId, setFilterCouncilId] = useState<number | ''>('');
  const [searchText, setSearchText] = useState('');
  const [filteredSchools, setFilteredSchools] = useState<AdminSchoolDTO[]>([]);

  useEffect(() => {
    loadSchools();
    loadCountries();
    loadCouncils();
    loadBoards();
  }, []);

  const loadBoards = async () => {
    try {
      const data = await getBoards();
      setBoards(data);
    } catch (err: unknown) {
      console.error('Failed to load boards:', err);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [schools, filterCountryId, filterCouncilId, councils, searchText]);

  useEffect(() => {
    if (filterCountryId) {
      loadCouncilsForCountry(filterCountryId as number);
    } else {
      loadCouncils();
      setFilterCouncilId('');
    }
  }, [filterCountryId]);

  const loadSchools = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSchools();
      setSchools(data);
      setFilteredSchools(data); // Initialize filtered schools
    } catch (err: any) {
      setError(err.message || 'Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      const data = await getAllCountries();
      setCountries(data);
    } catch (err: any) {
      console.error('Failed to load countries:', err);
    }
  };

  const loadCouncils = async () => {
    try {
      const data = await getCouncils();
      setCouncils(data);
    } catch (err: any) {
      console.error('Failed to load councils:', err);
    }
  };

  const loadCouncilsForCountry = async (countryId: number) => {
    try {
      const data = await getCouncils(countryId);
      setCouncils(data);
      // Reset council filter if the selected council is not in the filtered list
      if (filterCouncilId && !data.find(c => c.id === filterCouncilId)) {
        setFilterCouncilId('');
      }
    } catch (err: any) {
      console.error('Failed to load councils:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...schools];

    if (filterCountryId) {
      // Filter schools by country through their council
      const countryCouncilIds = councils
        .filter(c => c.countryId === filterCountryId)
        .map(c => c.id);
      filtered = filtered.filter(s => countryCouncilIds.includes(s.councilId));
    }

    if (filterCouncilId) {
      filtered = filtered.filter(s => s.councilId === filterCouncilId);
    }

    // Filter by school name (case-insensitive search)
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim();
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(searchLower)
      );
    }

    setFilteredSchools(filtered);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, school: AdminSchoolDTO) => {
    setAnchorEl(event.currentTarget);
    setSelectedSchool(school);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSchool(null);
  };

  const handleAddSchool = async () => {
    setSelectedSchool(null);
    setSelectedCouncilId('');
    setFormName('');
    setFormSchoolType('');
    setFormBoardId('');
    setFormAddress1('');
    setFormAddress2('');
    setFormCity('');
    setFormPostcode('');
    await loadCouncils();
    setOpenDialog(true);
  };

  const handleEditSchool = async () => {
    if (selectedSchool) {
      setSelectedCouncilId(selectedSchool.councilId);
      setFormName(selectedSchool.name || '');
      setFormSchoolType(selectedSchool.schoolType || '');
      setFormBoardId(selectedSchool.boardId ?? '');
      setFormAddress1(selectedSchool.addressLine1 || '');
      setFormAddress2(selectedSchool.addressLine2 || '');
      setFormCity(selectedSchool.city || '');
      setFormPostcode(selectedSchool.postcode || '');
    }
    setAnchorEl(null); // Close the menu
    await loadCouncils();
    setOpenDialog(true);
    // Don't reset selectedSchool here - we need it for the dialog title and save operation
  };

  const handleDeleteSchool = async () => {
    if (!selectedSchool || !selectedSchool.id) return;
    
    try {
      setLoading(true);
      setError(null);
      await deactivateSchool(selectedSchool.id);
      await loadSchools();
      handleMenuClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete school');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const schoolData: AdminSchoolDTO = {
        name: formName,
        councilId: typeof selectedCouncilId === 'number' ? selectedCouncilId : parseInt(selectedCouncilId.toString() || '0'),
        schoolType: formSchoolType,
        boardId: typeof formBoardId === 'number' ? formBoardId : undefined,
        addressLine1: formAddress1,
        addressLine2: formAddress2,
        city: formCity,
        postcode: formPostcode,
      };
      
      if (selectedSchool?.id) {
        await updateSchool(selectedSchool.id, schoolData);
      } else {
        await createSchool(schoolData);
      }
      
      await loadSchools();
      handleDialogClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save school');
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedSchool(null);
    setSelectedCouncilId('');
    setFormName('');
    setFormSchoolType('');
    setFormBoardId('');
    setFormAddress1('');
    setFormAddress2('');
    setFormCity('');
    setFormPostcode('');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <SchoolIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            School Management
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddSchool}
        >
          Add School
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ minWidth: 80 }}>
            Filters:
          </Typography>
          <TextField
            size="small"
            label="Search by School Name"
            placeholder="Enter school name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Country</InputLabel>
            <Select
              label="Country"
              value={filterCountryId}
              onChange={(e) => setFilterCountryId(e.target.value as number | '')}
            >
              <MenuItem value="">All Countries</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={!filterCountryId && !councils.length}>
            <InputLabel>Council</InputLabel>
            <Select
              label="Council"
              value={filterCouncilId}
              onChange={(e) => setFilterCouncilId(e.target.value as number | '')}
            >
              <MenuItem value="">All Councils</MenuItem>
              {councils.map((council) => (
                <MenuItem key={council.id} value={council.id}>
                  {council.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(filterCountryId || filterCouncilId || searchText) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setFilterCountryId('');
                setFilterCouncilId('');
                setSearchText('');
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>School Name</TableCell>
                <TableCell>Council</TableCell>
                <TableCell>School Type</TableCell>
                <TableCell>Board</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Postcode</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {filteredSchools.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {schools.length === 0 
                          ? 'No schools found. Click "Add School" to create one.'
                          : 'No schools match the selected filters.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSchools.map((school) => (
                    <TableRow key={school.id}>
                      <TableCell>{school.id}</TableCell>
                      <TableCell>{school.name}</TableCell>
                      <TableCell>{school.councilName || `Council ID: ${school.councilId}`}</TableCell>
                      <TableCell>{school.schoolType || '-'}</TableCell>
                      <TableCell>
                        {school.boardType
                          ? `${school.boardType}${school.boardName ? ` (${school.boardName})` : ''}`
                          : '-'}
                      </TableCell>
                      <TableCell>{school.city || '-'}</TableCell>
                      <TableCell>{school.postcode || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={school.status || 'ACTIVE'}
                          color={school.status === 'ACTIVE' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, school)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit School Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedSchool ? 'Edit School' : 'Add New School'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="School Name"
              fullWidth
              required
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
            <FormControl fullWidth required>
              <InputLabel>Council</InputLabel>
              <Select
                label="Council"
                value={selectedCouncilId}
                onChange={(e) => setSelectedCouncilId(e.target.value as number)}
              >
                <MenuItem value="">Select Council</MenuItem>
                {councils.map((council) => (
                  <MenuItem key={council.id} value={council.id}>
                    {council.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="School Type"
              fullWidth
              value={formSchoolType}
              onChange={(e) => setFormSchoolType(e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>Curriculum board</InputLabel>
              <Select
                label="Curriculum board"
                value={formBoardId}
                onChange={(e) => setFormBoardId(e.target.value as number | '')}
              >
                <MenuItem value="">Not set</MenuItem>
                {boards.map((board) => (
                  <MenuItem key={board.id} value={board.id}>
                    {board.boardType || board.name}
                    {board.name && board.boardType ? ` — ${board.name}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Address Line 1"
              fullWidth
              value={formAddress1}
              onChange={(e) => setFormAddress1(e.target.value)}
            />
            <TextField
              label="Address Line 2"
              fullWidth
              value={formAddress2}
              onChange={(e) => setFormAddress2(e.target.value)}
            />
            <TextField
              label="City"
              fullWidth
              value={formCity}
              onChange={(e) => setFormCity(e.target.value)}
            />
            <TextField
              label="Postcode"
              fullWidth
              value={formPostcode}
              onChange={(e) => setFormPostcode(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unknown Location Reports queue (spec 021) */}
      <Typography variant="h5" sx={{ mt: 6, mb: 2 }}>
        Unknown Location Reports
      </Typography>
      <UnknownLocationReports />

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditSchool}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteSchool} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default SchoolManagement;

